/**
 * Package Management Component
 * UI for managing tenant subscription packages
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import axios from 'axios';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  DollarSign,
  Database,
  HardDrive,
  Zap,
  Settings,
  AlertCircle,
  Save,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const PackageManagement = () => {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    packageCode: '',
    packageName: '',
    description: '',
    limitsConfig: {
      entities: {
        Customer: { limit: 1000, enforcement: 'soft' },
        Lead: { limit: 2000, enforcement: 'soft' },
        Opportunity: { limit: 500, enforcement: 'soft' },
        Activity: { limit: 5000, enforcement: 'soft' },
        SalesNote: { limit: 2000, enforcement: 'soft' },
        SupportTicket: { limit: 1000, enforcement: 'soft' },
        Knowledge: { limit: 500, enforcement: 'soft' },
        User: { limit: 50, enforcement: 'soft' }
      },
      storage: {
        total: { limit: 5368709120, enforcement: 'hard' } // 5GB in bytes
      },
      aiTokens: {
        monthly: { limit: 100000, enforcement: 'hard' },
        daily: { limit: 5000, enforcement: 'hard' }
      }
    },
    pricingConfig: {
      monthlyPrice: 0,
      currency: 'THB',
      billingCycle: 'monthly',
      setupFee: 0
    },
    isActive: true,
    isDefault: false
  });

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/packages');
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load packages:', error);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPackage(null);
    setFormData({
      packageCode: '',
      packageName: '',
      description: '',
      limitsConfig: {
        entities: {
          Customer: { limit: 1000, enforcement: 'soft' },
          Lead: { limit: 2000, enforcement: 'soft' },
          Opportunity: { limit: 500, enforcement: 'soft' },
          Activity: { limit: 5000, enforcement: 'soft' },
          SalesNote: { limit: 2000, enforcement: 'soft' },
          SupportTicket: { limit: 1000, enforcement: 'soft' },
          Knowledge: { limit: 500, enforcement: 'soft' },
          User: { limit: 50, enforcement: 'soft' }
        },
        storage: {
          total: { limit: 5368709120, enforcement: 'hard' }
        },
        aiTokens: {
          monthly: { limit: 100000, enforcement: 'hard' },
          daily: { limit: 5000, enforcement: 'hard' }
        }
      },
      pricingConfig: {
        monthlyPrice: 0,
        currency: 'THB',
        billingCycle: 'monthly',
        setupFee: 0
      },
      isActive: true,
      isDefault: false
    });
    setShowForm(true);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      packageCode: pkg.packageCode,
      packageName: pkg.packageName,
      description: pkg.description || '',
      limitsConfig: pkg.limitsConfig,
      pricingConfig: pkg.pricingConfig || {
        monthlyPrice: 0,
        currency: 'THB',
        billingCycle: 'monthly',
        setupFee: 0
      },
      isActive: pkg.isActive,
      isDefault: pkg.isDefault
    });
    setShowForm(true);
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      await axios.delete(`/api/packages/${packageId}`);
      toast.success('Package deleted successfully');
      loadPackages();
    } catch (error) {
      console.error('Failed to delete package:', error);
      toast.error(error.response?.data?.error || 'Failed to delete package');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPackage) {
        await axios.put(`/api/packages/${editingPackage.packageId}`, formData);
        toast.success('Package updated successfully');
      } else {
        await axios.post('/api/packages', formData);
        toast.success('Package created successfully');
      }
      setShowForm(false);
      loadPackages();
    } catch (error) {
      console.error('Failed to save package:', error);
      toast.error(error.response?.data?.error || 'Failed to save package');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Package className="w-8 h-8" />
            Package Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create and manage subscription packages for tenants
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Package
        </button>
      </div>

      {showForm && (
        <PackageForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          isEditing={!!editingPackage}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <PackageCard
            key={pkg.packageId}
            pkg={pkg}
            onEdit={handleEdit}
            onDelete={handleDelete}
            formatBytes={formatBytes}
            formatNumber={formatNumber}
          />
        ))}
      </div>

      {packages.length === 0 && !loading && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No packages found</p>
          <button
            onClick={handleCreate}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Create your first package
          </button>
        </div>
      )}
    </div>
  );
};

const PackageCard = ({ pkg, onEdit, onDelete, formatBytes, formatNumber }) => {
  const limits = pkg.limitsConfig || {};
  const pricing = pkg.pricingConfig || {};

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {pkg.packageName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {pkg.packageCode}
          </p>
        </div>
        <div className="flex gap-2">
          {pkg.isDefault && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
              Default
            </span>
          )}
          {pkg.isActive ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
        </div>
      </div>

      {pkg.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {pkg.description}
        </p>
      )}

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Database className="w-4 h-4 text-blue-500" />
          <span className="text-gray-600 dark:text-gray-400">Entities:</span>
          <span className="font-medium">
            {Object.keys(limits.entities || {}).length} types
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <HardDrive className="w-4 h-4 text-green-500" />
          <span className="text-gray-600 dark:text-gray-400">Storage:</span>
          <span className="font-medium">
            {formatBytes(limits.storage?.total?.limit || 0)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Zap className="w-4 h-4 text-yellow-500" />
          <span className="text-gray-600 dark:text-gray-400">AI Tokens:</span>
          <span className="font-medium">
            {formatNumber(limits.aiTokens?.monthly?.limit || 0)}/month
          </span>
        </div>
        {pricing.monthlyPrice > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-purple-500" />
            <span className="text-gray-600 dark:text-gray-400">Price:</span>
            <span className="font-medium">
              {pricing.monthlyPrice.toLocaleString()} {pricing.currency}/{pricing.billingCycle}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => onEdit(pkg)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => onDelete(pkg.packageId)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </div>
  );
};

const PackageForm = ({ formData, setFormData, onSubmit, onCancel, isEditing }) => {
  const updateLimit = (category, key, field, value) => {
    setFormData(prev => ({
      ...prev,
      limitsConfig: {
        ...prev.limitsConfig,
        [category]: {
          ...prev.limitsConfig[category],
          [key]: {
            ...prev.limitsConfig[category][key],
            [field]: value
          }
        }
      }
    }));
  };

  const updatePricing = (field, value) => {
    setFormData(prev => ({
      ...prev,
      pricingConfig: {
        ...prev.pricingConfig,
        [field]: value
      }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Package' : 'Create Package'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Package Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.packageCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, packageCode: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="BASIC"
                  disabled={isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Package Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.packageName}
                  onChange={(e) => setFormData(prev => ({ ...prev, packageName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Basic Package"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="3"
              />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Entity Limits
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(formData.limitsConfig.entities || {}).map(([entity, config]) => (
                  <div key={entity} className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {entity}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={config.limit}
                          onChange={(e) => updateLimit('entities', entity, 'limit', parseInt(e.target.value))}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <select
                          value={config.enforcement}
                          onChange={(e) => updateLimit('entities', entity, 'enforcement', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="soft">Soft</option>
                          <option value="hard">Hard</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Storage Limit
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Total Storage (GB)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={(formData.limitsConfig.storage.total.limit / 1024 / 1024 / 1024).toFixed(2)}
                      onChange={(e) => {
                        const gbValue = parseFloat(e.target.value) || 0;
                        const bytesValue = Math.round(gbValue * 1024 * 1024 * 1024);
                        updateLimit('storage', 'total', 'limit', bytesValue);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="5.00"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">GB</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.limitsConfig.storage.total.limit.toLocaleString()} bytes
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Enforcement
                  </label>
                  <select
                    value={formData.limitsConfig.storage.total.enforcement}
                    onChange={(e) => updateLimit('storage', 'total', 'enforcement', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="hard">Hard</option>
                    <option value="soft">Soft</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                AI Token Limits
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monthly Limit
                  </label>
                  <input
                    type="number"
                    value={formData.limitsConfig.aiTokens.monthly.limit}
                    onChange={(e) => updateLimit('aiTokens', 'monthly', 'limit', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Daily Limit
                  </label>
                  <input
                    type="number"
                    value={formData.limitsConfig.aiTokens.daily.limit}
                    onChange={(e) => updateLimit('aiTokens', 'daily', 'limit', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Pricing (Optional)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Monthly Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.pricingConfig.monthlyPrice}
                    onChange={(e) => updatePricing('monthlyPrice', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.pricingConfig.currency}
                    onChange={(e) => updatePricing('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="THB">THB</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Default Package</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isEditing ? 'Update' : 'Create'} Package
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PackageManagement;

